import type { CanvasRenderingTarget2D } from "fancy-canvas";
import type {
	Coordinate,
	IChartApi,
	IPrimitivePaneRenderer,
	IPrimitivePaneView,
	ISeriesApi,
	ISeriesPrimitive,
	ISeriesPrimitiveAxisView,
	SeriesOptionsMap,
	SeriesType,
	Time,
} from "lightweight-charts";
import { positionsLine } from "./helpers/lib";

// This is taken from the lightweight-charts plugin. They may not be supported anymore.
// https://github.com/tradingview/lightweight-charts/blob/master/plugin-examples/src/sample-data.ts

class VertLinePaneRenderer implements IPrimitivePaneRenderer {
	_x: Coordinate | null = null;
	_options: VertLineOptions;

	constructor(x: Coordinate | null, options: VertLineOptions) {
		this._x = x;
		this._options = options;
	}

	draw(target: CanvasRenderingTarget2D) {
		target.useBitmapCoordinateSpace((scope) => {
			if (this._x === null) return;
			const ctx = scope.context;
			const position = positionsLine(
				this._x,
				scope.horizontalPixelRatio,
				1, // Fixed width of 1px
			);

			ctx.fillStyle = this._options.color;
			const segmentLength = 4;
			const space = 3;
			let y = 0;

			while (y < scope.bitmapSize.height) {
				ctx.fillRect(position.position, y, 1, segmentLength);
				y += segmentLength + space;
			}
		});
	}
}

class VertLinePaneView implements IPrimitivePaneView {
	_source: VertLine;
	_x: Coordinate | null = null;
	_options: VertLineOptions;

	constructor(source: VertLine, options: VertLineOptions) {
		this._source = source;
		this._options = options;
	}
	update() {
		const timeScale = this._source._chart.timeScale();
		this._x = timeScale.timeToCoordinate(this._source._time);
	}
	renderer() {
		return new VertLinePaneRenderer(this._x, this._options);
	}
}

class VertLineTimeAxisView implements ISeriesPrimitiveAxisView {
	_source: VertLine;
	_x: Coordinate | null = null;
	_options: VertLineOptions;

	constructor(source: VertLine, options: VertLineOptions) {
		this._source = source;
		this._options = options;
	}
	update() {
		const timeScale = this._source._chart.timeScale();
		this._x = timeScale.timeToCoordinate(this._source._time);
	}
	visible() {
		return this._options.showLabel;
	}
	tickVisible() {
		return this._options.showLabel;
	}
	coordinate() {
		return this._x ?? 0;
	}
	text() {
		return this._options.labelText;
	}
	textColor() {
		return this._options.labelTextColor;
	}
	backColor() {
		return this._options.labelBackgroundColor;
	}
}

export interface VertLineOptions {
	color: string;
	labelText: string;
	width: number;
	labelBackgroundColor: string;
	labelTextColor: string;
	showLabel: boolean;
}

const defaultOptions: VertLineOptions = {
	color: "green",
	labelText: "",
	width: 3,
	labelBackgroundColor: "green",
	labelTextColor: "white",
	showLabel: false,
};

export class VertLine implements ISeriesPrimitive<Time> {
	_chart: IChartApi;
	_series: ISeriesApi<keyof SeriesOptionsMap>;
	_time: Time;
	_paneViews: VertLinePaneView[];
	_timeAxisViews: VertLineTimeAxisView[];

	constructor(
		chart: IChartApi,
		series: ISeriesApi<SeriesType>,
		time: Time,
		options?: Partial<VertLineOptions>,
	) {
		const vertLineOptions: VertLineOptions = {
			...defaultOptions,
			...options,
		};
		this._chart = chart;
		this._series = series;
		this._time = time;
		this._paneViews = [new VertLinePaneView(this, vertLineOptions)];
		this._timeAxisViews = [new VertLineTimeAxisView(this, vertLineOptions)];
	}
	updateAllViews() {
		for (const pw of this._paneViews) {
			pw.update();
		}
		for (const tw of this._timeAxisViews) {
			tw.update();
		}
	}
	timeAxisViews() {
		return this._timeAxisViews;
	}
	paneViews() {
		return this._paneViews;
	}
}
